// L13 · Speed Round (SPEC §3, Act V) — local, no judging, no strikes. Five binaries, three
// seconds each, big tap targets. Reaction time is captured per item: a silence (the clock ran
// out) counts as an answer, and the machine speaks ONCE at the end — to the fastest pick (the
// no-hesitation tell) or to the silence. The two most decisive picks become "detected" tags
// on the certificate (their evocative word-tags, kept out of the title matrix — §8).
//
// No verdict beat between items: a 1.2s considering animation five times would kill the pace
// the level is built on. The machine stays silent through the round and delivers one beat at
// the close.

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl } from "@/levels/util.ts";
import { L13 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

const ROUND_MS = 3000; // three seconds per binary (§3)
const INSTANT_MS = 750; // under this = "no hesitation", the tell worth naming

interface Pick {
  index: number;
  /** chosen option's certificate tag, or null if the clock ran out */
  tag: string | null;
  /** chosen option's label, or "—" on silence (for the facts bag / replay) */
  label: string;
  rt: number;
  timedOut: boolean;
}

export const L13: Level = {
  id: "L13",
  act: 5,
  title: "Speed Round",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      const picks: Pick[] = [];
      let roundTimer = 0;
      let rafId = 0;
      const clearRoundTimers = () => {
        if (roundTimer) window.clearTimeout(roundTimer);
        if (rafId) cancelAnimationFrame(rafId);
        roundTimer = 0;
        rafId = 0;
      };

      // ── intro ────────────────────────────────────────────────────────
      const intro = () => {
        host.replaceChildren();
        host.append(promptEl(V.intro));
        const go = el("button", "btn");
        go.textContent = V.go;
        go.style.marginTop = "8px";
        go.addEventListener("click", () => runRound(0), { once: true });
        host.append(go);
      };

      // ── one binary round ─────────────────────────────────────────────
      const runRound = (i: number) => {
        clearRoundTimers();
        const pair = V.pairs[i]!;
        host.replaceChildren();

        const wrap = el("div", "speed");

        const meta = el("div", "speed__meta");
        meta.textContent = `${i + 1} / ${V.pairs.length}`;

        const bar = el("div", "speed__bar");
        const fill = el("div", "speed__barfill");
        bar.append(fill);

        const row = el("div", "speed__pair");
        const mkOpt = (side: "a" | "b") => {
          const opt = pair[side];
          const b = el("button", "speed__opt");
          b.type = "button";
          b.textContent = opt.label;
          b.addEventListener("click", () => choose(i, opt.tag, opt.label));
          return b;
        };
        row.append(mkOpt("a"), mkOpt("b"));

        wrap.append(meta, bar, row);
        host.append(wrap);

        // Start the clock. rAF flips the fill from full to empty so the CSS width transition
        // runs (reduced motion snaps it — the JS timeout still governs the deadline).
        const start = now();
        fill.style.width = "100%";
        rafId = requestAnimationFrame(() => {
          fill.style.transitionDuration = `${ROUND_MS}ms`;
          fill.style.width = "0%";
        });
        roundTimer = window.setTimeout(() => choose(i, null, "—"), ROUND_MS);

        const choose = (idx: number, tag: string | null, label: string) => {
          if (picks.length > idx) return; // already answered this round (race guard)
          clearRoundTimers();
          const rt = tag === null ? ROUND_MS : Math.round(now() - start);
          picks.push({ index: idx, tag, label, rt, timedOut: tag === null });
          if (idx + 1 < V.pairs.length) runRound(idx + 1);
          else finish();
        };
      };

      // ── close: speak once, to the most telling moment ────────────────
      const finish = () => {
        const decisive = picks
          .filter((p) => !p.timedOut)
          .sort((a, b) => a.rt - b.rt);
        const speedTags = decisive.slice(0, 2).map((p) => p.tag!).filter(Boolean);

        let line: string;
        if (picks.some((p) => p.timedOut)) {
          line = V.silence;
        } else if (decisive[0] && decisive[0].rt < INSTANT_MS) {
          line = V.pairs[decisive[0].index]!.instant;
        } else {
          line = V.summary;
        }

        // L13's own closing beat (line omitted from the result so main.ts does not beat twice).
        void (async () => {
          await ctx.verdictBeat(line);
          done({
            pass: true, // no strikes in the speed round (§3)
            data: {
              speedTags,
              speedPicks: picks.map((p) => p.label),
            },
          });
        })();
      };

      intro();
      return clearRoundTimers;
    });
  },
};

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
